/**
 * <s-product-image-zoom> — PDP image zoom: preloads the fullscreen image, magnifies the
 * gallery image under the mouse on desktop, and toggles click/tap zoom with panning
 * inside the fullscreen dialog.
 *
 * Gallery buttons still open native dialogs declaratively via commandfor/show-modal.
 * This component only warms the selected zoom image on hover/focus so the
 * fullscreen dialog has the high-resolution image ready when opened.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    this.addEventListener('pointermove', this.#handleHoverMove, opts);
    this.addEventListener('pointerout', this.#handleHoverLeave, opts);
    this.addEventListener('pointerdown', this.#handleDialogDown, opts);
    this.addEventListener('click', this.#handleDialogClick, opts);
    this.addEventListener('pointermove', this.#handleDialogMove, opts);
    this.addEventListener('close', this.#handleDialogClose, { ...opts, capture: true });
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }


  // ---- Hover magnifier (desktop mouse) and click-to-zoom in the fullscreen view ----
  static HOVER_SCALE = 2.5;
  static DIALOG_SCALE = 2.5;

  #canHover() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  #setOrigin(image, event) {
    const rect = image.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
    image.style.transformOrigin = `${x}% ${y}%`;
  }

  #handleHoverMove = (event) => {
    if (event.pointerType !== 'mouse' || !this.#canHover()) return;
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-zoom-preload]');
    const image = trigger?.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return;

    if (!image.dataset.hoverZoom) {
      image.dataset.hoverZoom = 'true';
      // Ask the browser for a sharper rendition while magnified.
      if (image.srcset) image.sizes = '1600px';
    }
    this.#setOrigin(image, event);
    image.style.scale = String(SProductImageZoom.HOVER_SCALE);
  };

  #handleHoverLeave = (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-zoom-preload]');
    if (!trigger || trigger.contains(event.relatedTarget)) return;
    const image = trigger.querySelector('img');
    if (image) image.style.scale = '';
  };

  #dragStart = null;

  #handleDialogDown = (event) => {
    this.#dragStart = { x: event.clientX, y: event.clientY };
  };

  #handleDialogClick = (event) => {
    if (!(event.target instanceof HTMLImageElement) || !event.target.matches('[data-zoom-image]')) return;
    const start = this.#dragStart;
    this.#dragStart = null;
    // A drag (panning a zoomed image) should not toggle zoom off.
    if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 8) return;
    const image = event.target;
    if (image.hasAttribute('data-zoomed')) {
      this.#resetDialogImage(image);
    } else {
      this.#setOrigin(image, event);
      image.toggleAttribute('data-zoomed', true);
      image.style.scale = String(SProductImageZoom.DIALOG_SCALE);
    }
  };

  #handleDialogMove = (event) => {
    if (!(event.target instanceof HTMLImageElement) || !event.target.hasAttribute('data-zoomed')) return;
    // Mouse pans on hover; touch and pen pan while dragging.
    if (event.pointerType !== 'mouse' && event.buttons === 0) return;
    this.#setOrigin(event.target, event);
  };

  #resetDialogImage(image) {
    image.removeAttribute('data-zoomed');
    image.style.scale = '';
  }

  #handleDialogClose = (event) => {
    if (!(event.target instanceof HTMLDialogElement)) return;
    for (const image of event.target.querySelectorAll('img[data-zoomed]')) this.#resetDialogImage(image);
  };

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
